import { call, put, select } from "redux-saga/effects";
import { findNode, getBookIdWithPrefix, getBookIdNumber } from "../utils/content/content";
import {
  getChapterContentRequest,
  getWaveformRequest
} from "../api/not-sorted";
import { withTakePreviousCallIfSameParams } from "../utils/saga";
import { _sharedActionsPlayer } from "./player/actions";
import { _sharedActionsSystem } from "./system/actions";
import {
  selectorMainTreeBooks,
  selectorMainTree,
  selectorMainTreeIdsBooksFetchFailed
} from "./mainTree/selectors";
import { selectorTocItems } from "./toc/selectors";
import { selectorChaptersParas } from "./paragraphs/selectors";
import { consoleLogError } from "../utils/console";
import { selectorPlayerWaveformsDictionary } from "./player/selectors";
import {getParagraphsRequest} from "../api/content/common";
import {LoadDirection} from "../api/content/constants";
import { _sharedActionsMainTree } from "./mainTree/actions";
import { getBookWaveformLoaderId } from "@/shared/store/saga-functions.utils";
import { getBookByIdRequest, getBookTocById } from "@/shared/api/content/books/requests";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { getLanguageDefault } from "@/shared/utils/user-agent";
import { makeLoaderIdBook, makeLoaderIdBookTOC } from "@/shared/store/mainTree/utils";

function* _fetchAndStoreWaveformsDictionary (url, chapterId) {
  if (url && chapterId) {
    let loader = getBookWaveformLoaderId(chapterId);

    try {
      const waveformsDictionary = yield select(selectorPlayerWaveformsDictionary);
      if (waveformsDictionary[chapterId]) {
        return waveformsDictionary[chapterId];
      }

      yield put(_sharedActionsSystem.loading(loader));

      const nBookId = getBookIdNumber(chapterId);
      const response = yield call(getWaveformRequest, url, nBookId);

      const { waveform, paragraphsAudioPositions } = response || {};

      yield put(_sharedActionsPlayer.putAudioWaveformsData({
        waveform,
        paragraphsAudioPositions,
        chapterId,
      }));

      return {
        waveform,
        paragraphsAudioPositions,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      consoleLogError("Shared Saga", "Error load waveform " + chapterId + " url: " + url, error);
      return undefined;
    } finally {
      yield put(_sharedActionsSystem.loading(loader, true));
    }
  }

  return undefined;
}
export const fetchAndStoreWaveformsDictionary = (
  url, chapterId
) => withTakePreviousCallIfSameParams(_fetchAndStoreWaveformsDictionary, url, chapterId);

export function* _getBookById (_bookId, attachChildrenToBook = true) {
  if (!_bookId) {
    return undefined;
  }

  const bookIdOrigin = getBookIdNumber(_bookId);

  try {
    const idsBooksFetchFailed = yield select(selectorMainTreeIdsBooksFetchFailed);

    if (idsBooksFetchFailed.includes(bookIdOrigin)) {
      return null;
    }

    const bookId = getBookIdWithPrefix(_bookId);
    const mainTree = yield select(selectorMainTree);
    const books = yield select(selectorMainTreeBooks);

    let book = books.find((b) => b.book_id === bookIdOrigin);

    if (!book) {
      const treeBook = findNode(bookId, mainTree);
      if (treeBook) {
        book = {...treeBook};
      }
    }

    if (!book) {
      const loaderId = makeLoaderIdBook(bookId);
      yield put(_sharedActionsSystem.loading(loaderId));
      book = yield call(getBookByIdRequest, bookId);
      // may be need to save a book firstly, then remove the loader?
      yield put(_sharedActionsSystem.loading(loaderId, true));
    }

    if (book && attachChildrenToBook && !book.chapters?.length) {
      const loaderId = makeLoaderIdBookTOC(bookId);
      yield put(_sharedActionsSystem.loading(loaderId));
      const chapterData = yield call(getBookTocById, bookIdOrigin);
      if (chapterData) {
        book.children = chapterData.children;
        book.chapters = chapterData.chapters;
      }
      // may be need to save a book firstly, then remove the loader?
      yield put(_sharedActionsSystem.loading(loaderId, true));
    }

    if (!book) {
      yield put(_sharedActionsMainTree.addIdBrokenBook(bookIdOrigin));
    }

    return book;
  } catch (error) {
    yield put(_sharedActionsMainTree.addIdBrokenBook(bookIdOrigin));
    return undefined;
  }
}
// {attachChildrenToBook} - is for REACT app old logic of a book having chapters and children,
// but it's changed - book chapters is a TOC and is stored in a store reducer {state.toc}.
export const getBookById = (bookIdOrigin, attachChildrenToBook) => {
  return withTakePreviousCallIfSameParams(_getBookById, bookIdOrigin, attachChildrenToBook);
};

export function* _getBookToc (_bookId) {
  const bookIdOrigin = getBookIdNumber(_bookId);
  if (!bookIdOrigin) {
    return undefined;
  }

  try {
    const tocItems = yield select(selectorTocItems);

    let toc = tocItems[bookIdOrigin];
    if (!toc) {
      const loaderId = makeLoaderIdBookTOC(bookIdOrigin);

      yield put(_sharedActionsSystem.loading(loaderId));
      const data = yield call(getBookTocById, bookIdOrigin);
      toc = data.chapters;
      yield put(_sharedActionsSystem.loading(loaderId, true));
    }
    return toc;
  } catch (error) {
    return undefined;
  }
}
export const getBookToc = (bookIdOrigin) =>
  withTakePreviousCallIfSameParams(_getBookToc, bookIdOrigin);

export function* _getChapterParas (chapterId, highlight, isFetchAllPara = false) {
  if (!chapterId) {
    return undefined;
  }

  try {
    const chaptersParas = yield select(selectorChaptersParas);
    const bookIdOrigin = getBookIdNumber(chapterId);
    const chapterNumber = chapterId.split(".")[1];
    const bookChaptersParas = chaptersParas[bookIdOrigin];

    let content = bookChaptersParas?.[chapterNumber];
    if (!content || content.highlight !== highlight) {
      let response = null;
      if (isFetchAllPara) {
        response = yield call(getParagraphsRequest, chapterId, 100, LoadDirection.both, highlight);
      } else {
        response = yield call(getChapterContentRequest, chapterId, undefined, undefined, highlight);
      }
      if (response) {
        content = {
          paras: response,
          highlight,
        };
      }
    }
    return content;
  } catch (e) {
    return undefined;
  }
}
export const getChapterParas = (chapterId, highlight, isFetchAllPara = false) =>
  withTakePreviousCallIfSameParams(_getChapterParas, chapterId, highlight, isFetchAllPara);

export function* getDefaultLanguage () {
  const libraryLanguages = yield select(selectorSetting(SETTING.libraryLanguages.id));
  return getLanguageDefault(libraryLanguages);
}
